import { useEffect, useRef, useState } from 'react';

const KEY = 'milora-advisor-position-v1';
const DEFAULT = { right: 24, bottom: 24 };
const SIZE = 52;
const GAP = 12;

function clamp(position) {
  const bound = (value, viewport) => Math.min(Math.max(GAP, viewport - SIZE - GAP), Math.max(GAP, value));
  return { right: bound(position.right, window.innerWidth), bottom: bound(position.bottom, window.innerHeight) };
}

function initialPosition() {
  try {
    const saved = JSON.parse(localStorage.getItem(KEY));
    if (Number.isFinite(saved?.right) && Number.isFinite(saved?.bottom)) return clamp(saved);
  } catch { /* Storage can be unavailable; dragging still works. */ }
  return clamp(DEFAULT);
}

function save(position) {
  try { localStorage.setItem(KEY, JSON.stringify(position)); } catch { /* Optional preference only. */ }
}

export function useDraggableLauncher(onOpen) {
  const [position, setPosition] = useState(initialPosition);
  const current = useRef(position);
  const drag = useRef(null);
  const suppressClick = useRef(false);
  const move = next => { current.current = clamp(next); setPosition(current.current); };

  useEffect(() => {
    const resize = () => {
      current.current = clamp(current.current);
      setPosition(current.current);
      save(current.current);
    };
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, []);

  const finish = (event, cancelled = false) => {
    const state = drag.current;
    if (!state || state.id !== event.pointerId) return;
    drag.current = null;
    suppressClick.current = state.moved;
    if (cancelled) move(state.origin);
    if (state.moved) save(current.current);
    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
  };

  return {
    style: { right: position.right, bottom: position.bottom, touchAction: 'none', userSelect: 'none' },
    onPointerDown: event => {
      if (event.button !== 0 || event.isPrimary === false || drag.current) return;
      suppressClick.current = false;
      drag.current = { id: event.pointerId, x: event.clientX, y: event.clientY, origin: current.current, moved: false };
      event.currentTarget.setPointerCapture?.(event.pointerId);
    },
    onPointerMove: event => {
      const state = drag.current;
      if (!state || state.id !== event.pointerId) return;
      const dx = event.clientX - state.x, dy = event.clientY - state.y;
      if (!state.moved && Math.hypot(dx, dy) < 6) return;
      state.moved = true;
      event.preventDefault();
      move({ right: state.origin.right - dx, bottom: state.origin.bottom - dy });
    },
    onPointerUp: event => finish(event),
    onPointerCancel: event => finish(event, true),
    onLostPointerCapture: event => finish(event, true),
    onClick: event => {
      if (suppressClick.current && event.detail !== 0) {
        event.preventDefault();
        suppressClick.current = false;
        return;
      }
      onOpen();
    },
    onKeyDown: event => {
      if (!event.altKey) return;
      const offsets = { ArrowLeft: [16, 0], ArrowRight: [-16, 0], ArrowUp: [0, 16], ArrowDown: [0, -16] };
      if (event.key !== 'Home' && !offsets[event.key]) return;
      event.preventDefault();
      if (event.key === 'Home') move(DEFAULT);
      else {
        const [x, y] = offsets[event.key];
        move({ right: current.current.right + x, bottom: current.current.bottom + y });
      }
      save(current.current);
    },
  };
}
